import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Button from './Button';
// Temporarily using text-based logo until a new logo image is created
// import logo from '../assets/logos/vsq-logo-vectorBASEWhiteLogo.png';

// Add CSS for mobile menu
const mobileMenuStyles = `
  /* Hide mobile elements by default for all screen sizes */
  .mobile-menu-overlay {
    display: none;
  }
  
  .mobile-menu {
    display: none;
  }
  
  .hamburger-button {
    display: none;
    background: none;
    border: none;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.5rem;
  }
  
  /* Mobile menu styles */
  .mobile-menu-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.7);
    z-index: 999;
  }
  
  .mobile-menu {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 80%;
    max-width: 300px;
    background-color: #1e1e1e;
    z-index: 1000;
    padding: 1rem;
    overflow-y: auto;
    transform: translateX(100%);
    transition: transform 0.3s ease-in-out;
  }
  
  .mobile-menu.open {
    transform: translateX(0);
    display: block;
  }
  
  .mobile-menu-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .mobile-menu-close {
    background: none;
    border: none;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
  }
  
  .mobile-menu-section {
    margin-bottom: 1rem;
  }
  
  .mobile-menu-section-title {
    color: #7FE7D9;
    font-weight: bold;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
  }
  
  .mobile-menu-items {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  
  .mobile-menu-item {
    margin-bottom: 0.75rem;
  }
  
  .mobile-menu-link {
    color: white;
    text-decoration: none;
    font-size: 1rem;
    display: block;
    padding: 0.5rem 0;
  }
  
  /* Media query for mobile devices */
  @media (max-width: 768px) {
    .desktop-nav {
      display: none;
    }
    
    .hamburger-button {
      display: block;
    }
    
    .mobile-menu-overlay.active {
      display: block;
    }
    
    .mobile-menu.open {
      display: block;
    }
  }
`;

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Track window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLoginClick = () => {
    navigate('/login');
    setMobileMenuOpen(false);
  };

  const handleMenuHover = (menu: string) => {
    if (windowWidth > 768) {
      setActiveMenu(menu);
    }
  };

  const handleMenuLeave = () => {
    if (windowWidth > 768) {
      setActiveMenu(null);
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    // Prevent body scrolling when menu is open
    document.body.style.overflow = !mobileMenuOpen ? 'hidden' : '';
  };

  const handleMobileMenuItemClick = () => {
    setMobileMenuOpen(false);
    document.body.style.overflow = '';
  };

  const menuItems = {
    explore: ['Franchise Finder', 'Location Analysis', 'Competition Research'],
    financing: ['SBA Loans', 'Lease vs Buy Calculator'],
    tools: ['FDD Analyzer', 'Void Analysis', 'Mobile Data', 'Marketing Dashboard'],
    aiAgents: ['How It Works', '24/7 Monitoring', 'Revenue Optimization'],
    industries: ['Entertainment Centers', 'Trampoline Parks', 'Bowling', 'Arcade'],
    insights: ['Case Studies', 'Playbooks', 'Market Benchmarks']
  };

  return (
    <>
      {/* Add style tag for mobile menu styles */}
      <style>{mobileMenuStyles}</style>
      
      <header style={{
        backgroundColor: '#1e1e1e',
        color: 'white',
        padding: '1rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          maxWidth: '1400px',
          margin: '0 auto',
          width: '100%'
        }}>
          <div className="logo">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                fontWeight: 'bold', 
                fontSize: '24px',
                color: 'white'
              }}>
                <span style={{ color: '#7FE7D9' }}>Ask</span>
                <span>CEO.ai</span>
              </div>
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="desktop-nav">
            <ul style={{ listStyle: 'none', display: 'flex', gap: '1.5rem', margin: 0, padding: 0, alignItems: 'center' }}>
              {user && <li><Link to="/welcome" style={{ color: 'white', textDecoration: 'none' }}>Dashboard</Link></li>}
              
              {/* Explore Menu */}
              <li 
                onMouseEnter={() => handleMenuHover('explore')}
                onMouseLeave={handleMenuLeave}
                style={{ position: 'relative' }}
              >
                <Link to="/explore" style={{ color: 'white', textDecoration: 'none' }}>Explore</Link>
                {activeMenu === 'explore' && (
                  <DropdownMenu 
                    items={menuItems.explore} 
                    baseUrl="/explore" 
                  />
                )}
              </li>
              
              {/* Financing Menu */}
              <li 
                onMouseEnter={() => handleMenuHover('financing')}
                onMouseLeave={handleMenuLeave}
                style={{ position: 'relative' }}
              >
                <Link to="/financing" style={{ color: 'white', textDecoration: 'none' }}>Financing</Link>
                {activeMenu === 'financing' && (
                  <DropdownMenu 
                    items={menuItems.financing} 
                    baseUrl="/financing" 
                  />
                )}
              </li>
              
              {/* Tools Menu */}
              <li 
                onMouseEnter={() => handleMenuHover('tools')}
                onMouseLeave={handleMenuLeave}
                style={{ position: 'relative' }}
              >
                <Link to="/tools" style={{ color: 'white', textDecoration: 'none' }}>Tools</Link>
                {activeMenu === 'tools' && (
                  <DropdownMenu 
                    items={menuItems.tools} 
                    baseUrl="/tools" 
                  />
                )}
              </li>
              
              {/* AI Agents Menu */}
              <li 
                onMouseEnter={() => handleMenuHover('aiAgents')}
                onMouseLeave={handleMenuLeave}
                style={{ position: 'relative' }}
              >
                <Link to="/ai-agents" style={{ color: 'white', textDecoration: 'none' }}>AI Agents</Link>
                {activeMenu === 'aiAgents' && (
                  <DropdownMenu 
                    items={menuItems.aiAgents} 
                    baseUrl="/ai-agents" 
                  />
                )}
              </li>
              
              {/* Industries Menu */}
              <li 
                onMouseEnter={() => handleMenuHover('industries')}
                onMouseLeave={handleMenuLeave}
                style={{ position: 'relative' }}
              >
                <Link to="/industries" style={{ color: 'white', textDecoration: 'none' }}>Industries</Link>
                {activeMenu === 'industries' && (
                  <DropdownMenu 
                    items={menuItems.industries} 
                    baseUrl="/industries" 
                  />
                )}
              </li>
              
              {/* Insights Menu */}
              <li 
                onMouseEnter={() => handleMenuHover('insights')}
                onMouseLeave={handleMenuLeave}
                style={{ position: 'relative' }}
              >
                <Link to="/insights" style={{ color: 'white', textDecoration: 'none' }}>Insights</Link>
                {activeMenu === 'insights' && (
                  <DropdownMenu 
                    items={menuItems.insights} 
                    baseUrl="/insights" 
                  />
                )}
              </li>
              
              <li><Link to="/pricing" style={{ color: 'white', textDecoration: 'none' }}>Pricing</Link></li>
              
              {user ? (
                <>
                  <li><Link to="/profile" style={{ color: 'white', textDecoration: 'none' }}>Profile</Link></li>
                  <li><Button onClick={logout}>Logout</Button></li>
                </>
              ) : (
                <li><Button onClick={handleLoginClick}>Try AI Agent Demo</Button></li>
              )}
            </ul>
          </nav>
          
          {/* Mobile Menu Button */}
          <button 
            className="hamburger-button" 
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            ☰
          </button>
        </div>
      </header>
      
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay active" onClick={toggleMobileMenu}></div>
      )}
      
      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-header">
          <div style={{ 
            fontWeight: 'bold', 
            fontSize: '20px',
            color: 'white'
          }}>
            <span style={{ color: '#7FE7D9' }}>Ask</span>
            <span>CEO.ai</span>
          </div>
          <button 
            className="mobile-menu-close" 
            onClick={toggleMobileMenu}
            aria-label="Close mobile menu"
          >
            ✕
          </button>
        </div>
        
        {user && (
          <div className="mobile-menu-section">
            <ul className="mobile-menu-items">
              <li className="mobile-menu-item">
                <Link 
                  to="/welcome" 
                  className="mobile-menu-link"
                  onClick={handleMobileMenuItemClick}
                >
                  Dashboard
                </Link>
              </li>
            </ul>
          </div>
        )}
        
        {/* Explore Section */}
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Explore</div>
          <ul className="mobile-menu-items">
            {menuItems.explore.map((item, index) => (
              <li key={index} className="mobile-menu-item">
                <Link 
                  to={`/explore/${item.toLowerCase().replace(/\s+/g, '-')}`}
                  className="mobile-menu-link"
                  onClick={handleMobileMenuItemClick}
                >
                  {item}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Financing Section */}
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Financing</div>
          <ul className="mobile-menu-items">
            {menuItems.financing.map((item, index) => (
              <li key={index} className="mobile-menu-item">
                <Link 
                  to={`/financing/${item.toLowerCase().replace(/\s+/g, '-')}`}
                  className="mobile-menu-link"
                  onClick={handleMobileMenuItemClick}
                >
                  {item}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Tools Section */}
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Tools</div>
          <ul className="mobile-menu-items">
            {menuItems.tools.map((item, index) => (
              <li key={index} className="mobile-menu-item">
                <Link 
                  to={`/tools/${item.toLowerCase().replace(/\s+/g, '-')}`}
                  className="mobile-menu-link"
                  onClick={handleMobileMenuItemClick}
                >
                  {item}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        
        {/* AI Agents Section */}
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">AI Agents</div>
          <ul className="mobile-menu-items">
            {menuItems.aiAgents.map((item, index) => (
              <li key={index} className="mobile-menu-item">
                <Link 
                  to={`/ai-agents/${item.toLowerCase().replace(/\s+/g, '-')}`}
                  className="mobile-menu-link"
                  onClick={handleMobileMenuItemClick}
                >
                  {item}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Industries Section */}
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Industries</div>
          <ul className="mobile-menu-items">
            {menuItems.industries.map((item, index) => (
              <li key={index} className="mobile-menu-item">
                <Link 
                  to={`/industries/${item.toLowerCase().replace(/\s+/g, '-')}`}
                  className="mobile-menu-link"
                  onClick={handleMobileMenuItemClick}
                >
                  {item}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Insights Section */}
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Insights</div>
          <ul className="mobile-menu-items">
            {menuItems.insights.map((item, index) => (
              <li key={index} className="mobile-menu-item">
                <Link 
                  to={`/insights/${item.toLowerCase().replace(/\s+/g, '-')}`}
                  className="mobile-menu-link"
                  onClick={handleMobileMenuItemClick}
                >
                  {item}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Other Links */}
        <div className="mobile-menu-section">
          <ul className="mobile-menu-items">
            <li className="mobile-menu-item">
              <Link 
                to="/pricing" 
                className="mobile-menu-link"
                onClick={handleMobileMenuItemClick}
              >
                Pricing
              </Link>
            </li>
            {user ? (
              <>
                <li className="mobile-menu-item">
                  <Link 
                    to="/profile" 
                    className="mobile-menu-link"
                    onClick={handleMobileMenuItemClick}
                  >
                    Profile
                  </Link>
                </li>
                <li className="mobile-menu-item" style={{ marginTop: '1rem' }}>
                  <Button 
                    onClick={() => {
                      logout();
                      handleMobileMenuItemClick();
                    }}
                    fullWidth
                  >
                    Logout
                  </Button>
                </li>
              </>
            ) : (
              <li className="mobile-menu-item" style={{ marginTop: '1rem' }}>
                <Button 
                  onClick={handleLoginClick}
                  fullWidth
                >
                  Try AI Agent Demo
                </Button>
              </li>
            )}
          </ul>
        </div>
      </div>
    </>
  );
};

// Dropdown Menu Component
const DropdownMenu: React.FC<{ 
  items: string[]; 
  baseUrl: string;
}> = ({ items, baseUrl }) => (
  <div style={{
    position: 'absolute',
    top: '100%',
    left: 0,
    backgroundColor: '#1e1e1e',
    padding: '1rem',
    borderRadius: '8px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
    zIndex: 1000,
    minWidth: '200px'
  }}>
    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {items.map((item, index) => (
        <li key={index} style={{ marginBottom: '0.5rem' }}>
          <Link 
            to={`${baseUrl}/${item.toLowerCase().replace(/\s+/g, '-')}`}
            style={{ color: 'white', textDecoration: 'none', fontSize: '1rem', transition: 'color 0.3s' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#7FE7D9'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'white'}
          >
            {item}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

export default Header;
